import Navigation from "../Shared/Navigation";
import { Button } from "../ui";
import SectionHeading from "../Shared/SectionHeading";
import BookCard from "./BookCard";
import RecommendBookCard from "./RecommendBookCard";
import CatalogueShowcase from "./CatalogueShowcase";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faCompass,
  faRightToBracket,
  faPenNib,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import { apiClient } from "../../util/apiClient";
import { getProject } from "../../util/chaptersApi";
import { listReadings } from "../../util/readingsApi";
import { useUser } from "../../store/hooks";
import { useAuth } from "../../auth/AuthContext";
import { profilePath } from "../../util/urlIds";
import { avatarSrc } from "../../util/avatar";
import { logger } from "../../util/logger";
import Seo from "../../seo/Seo";
import Page from "../../a11y/Page";
import { useT, useI18n } from "../../i18n/I18nContext";

const RECOMMENDED_LIMIT = 12;
// Cap how many books from the same author the recommendation
// strip will surface. Without this a prolific writer dominates
// the rail and crowds out everyone else.
const MAX_PER_AUTHOR = 2;

// Recommendation scorer. Reads the public-projects payload (which
// already carries every signal we use) and produces a per-project
// score; higher = surface first. Pure function, easy to test, no
// new API calls.
//
// Signals (weights tuned against an early-stage catalogue — small
// pool, light reader signal, mostly recency + quality matter):
//   * locale match            +30   (writer reads in their UI lang)
//   * genre / category overlap with the writer's own work +15
//   * rating quality          up to +20 (avg × confidence factor)
//   * popularity              up to +20 (log-scaled reader_count)
//   * recency (last update)   up to +10 (30d window)
//   * chapter depth           +8 if ≥3 published chapters (signals
//                                 commitment, vs a single-chapter teaser)
//   * jitter                  ±2 so the order isn't frozen forever —
//                                 same pool, same writer, different
//                                 days = slightly different selection
function _scoreProject(p, { currentLocale, ownGenres, ownCategories }) {
  let score = 0;

  // Locale match — the strongest single boost because it
  // determines whether the writer can actually engage with the book.
  const lang = (p.language || "en").toLowerCase().slice(0, 2);
  if (lang === currentLocale) score += 30;

  // Genre / category overlap with what the writer themselves has
  // published. Pulls the rail toward the writer's own taste.
  const g = (p.genre || "").toLowerCase();
  const c = (p.category || "").toLowerCase();
  if (g && ownGenres.has(g)) score += 15;
  if (c && ownCategories.has(c)) score += 10;

  // Rating quality with a confidence factor — a 5★ project with 1
  // rating shouldn't beat a 4.5★ project with 50 ratings.
  const ratingAvg = Number(p.rating_average) || 0;
  const ratingCount = Number(p.rating_count) || 0;
  if (ratingCount > 0) {
    const confidence = Math.min(1, Math.log10(ratingCount + 1) / 1.5);
    score += ratingAvg * 4 * confidence;  // max ~20
  }

  // Popularity (reader_count) on log scale — prevents the most
  // popular book from dominating purely on reach.
  const readers = Number(p.reader_count) || 0;
  if (readers > 0) {
    score += Math.min(20, Math.log10(readers + 1) * 5);
  }

  // Recency — actively-maintained projects boost. Window: 30 days.
  const lastUpdate =
    p.last_chapter_updated_at || p.updated_at || p.created_at;
  if (lastUpdate) {
    const ageMs = Date.now() - new Date(lastUpdate).getTime();
    const ageDays = ageMs / 86_400_000;
    if (ageDays < 30) score += Math.max(0, 10 * (1 - ageDays / 30));
  }

  // Chapter depth — at least a few published chapters means
  // there's substance to read.
  const published =
    Number(p.published_chapter_count ?? p.chapter_count) || 0;
  if (published >= 3) score += 8;

  // Light jitter so the order isn't visually identical to
  // yesterday's. Seeded by project id + UTC day so it's stable
  // within a day but shifts overnight.
  const dayBucket = Math.floor(Date.now() / 86_400_000);
  const jitterSeed = ((p.id || 0) * 2654435761 + dayBucket) >>> 0;
  score += ((jitterSeed % 401) - 200) / 100; // ±2

  return score;
}

// De-duplication helper: cap how many entries we keep from each
// owner so a single prolific writer doesn't claim half the rail.
function _dedupeByAuthor(sorted, maxPerAuthor) {
  const counts = new Map();
  const out = [];
  for (const p of sorted) {
    const key = (p.owner_email || "").toLowerCase();
    const n = counts.get(key) || 0;
    if (n >= maxPerAuthor) continue;
    counts.set(key, n + 1);
    out.push(p);
  }
  return out;
}

const SkeletonCard = () => (
  <div
    className="border border-border rounded-xl p-4 animate-pulse bg-surface"
    aria-hidden="true"
  >
    <div className="w-full aspect-[3/4] rounded-lg bg-surfaceAlt mb-3" />
    <div className="h-4 w-3/4 rounded bg-surfaceAlt mb-2" />
    <div className="h-3 w-1/2 rounded bg-surfaceAlt" />
  </div>
);

const SkeletonGrid = ({ count = 6 }) => (
  <div
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-5 mb-8"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    {Array.from({ length: count }, (_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

// Themed empty state with a centred icon medallion so the blank sections
// never look broken — they look intentional.
const EmptyState = ({ icon, title, description, action }) => (
  <div className="mb-8 rounded-xl border border-dashed border-border bg-surfaceAlt px-6 py-12">
    <div className="flex flex-col items-center text-center max-w-md mx-auto">
      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-surface border border-border mb-5 text-primary">
        <FontAwesomeIcon icon={icon} className="text-3xl" aria-hidden="true" />
      </div>
      <h3 className="text-lg font-semibold text-text mb-1">{title}</h3>
      <p className="text-sm text-secondText mb-5">{description}</p>
      {action}
    </div>
  </div>
);

const UserHome = () => {
  const t = useT();
  const navigate = useNavigate();
  const { status } = useAuth();
  const user = useUser();
  const { locale } = useI18n();
  const isSignedIn = status === "signedIn";

  const [yourStoriesBook, setYourStoriesBook] = useState([]);
  // Anchors for the "Start reading" / "Find something to read"
  // CTAs' fallback when the writer doesn't have anything on their
  // shelf yet — both buttons scroll the recommendations grid into
  // view first, then the catalogue showcase as a second-tier
  // fallback if recommendations is empty too. Without those refs
  // the buttons silently no-op'd on a fresh-account state.
  const recommendedSectionRef = useRef(null);
  const catalogueSectionRef = useRef(null);
  const [recommendedForYouBooks, setRecommendedForYouBooks] = useState([]);
  // Full public-projects payload (already fetched for "Recommended");
  // also feeds the Catalogue Showcase rail at the top so we don't
  // pay a second round trip just to populate the vitrine.
  const [cataloguePool, setCataloguePool] = useState([]);
  const [loadingReadings, setLoadingReadings] = useState(isSignedIn);
  const [loadingRecommended, setLoadingRecommended] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Catalogue de-dup set — every project id already displayed in
  // the shelves above (Your stories + Recommended). The Catalogue
  // showcase rail filters these out so the same project doesn't
  // appear twice on /browse (e.g. Les Liaisons dangereuses
  // surfacing once in "Recommended" and again three rows down in
  // the catalogue). Coerced to strings because shelf ids round-trip
  // through the API as numbers but the catalogue ids are mixed.
  const excludeFromCatalogue = useMemo(() => {
    const ids = new Set();
    for (const b of yourStoriesBook) if (b?.id != null) ids.add(String(b.id));
    for (const b of recommendedForYouBooks) if (b?.id != null) ids.add(String(b.id));
    return ids;
  }, [yourStoriesBook, recommendedForYouBooks]);

  // Scroll the writer toward something to read. Recommended grid
  // first (most personal); falls back to the catalogue showcase if
  // recommendations couldn't fill at all (brand-new platform with
  // no public projects, every public project happens to be on the
  // caller's shelf, etc.). Re-used by both the welcome "Start
  // reading" button and the empty-shelf "Find something to read"
  // CTA so the two affordances behave identically.
  const scrollToFindReading = () => {
    const target =
      recommendedForYouBooks.length > 0
        ? recommendedSectionRef.current
        : catalogueSectionRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let cancelled = false;
    const selfEmail = (sessionStorage.getItem("email") || "").toLowerCase();

    // Single effect, two fetches in parallel:
    //   * shelf  — only when signed in.
    //   * public projects, popularity-ordered  — always.
    // Earlier each section had its own loader and ``listReadings()``
    // ran twice when signed in (once for the shelf, once for the
    // shelf-filter on recommendations). Sharing the rows here halves
    // the round trips on the happy path.
    const load = async () => {
      setLoadingReadings(isSignedIn);
      setLoadingRecommended(true);
      try {
        const [rows, projectsResp] = await Promise.all([
          isSignedIn ? listReadings().catch(() => []) : Promise.resolve([]),
          apiClient
            .get("/api/projects/", {
              params: {
                visibility: "public",
                order: "popular",
                limit: RECOMMENDED_LIMIT * 2,
              },
            })
            .catch((err) => {
              logger.error("userhome_recommended_failed", err?.message);
              return { data: { projects: [] } };
            }),
        ]);
        if (cancelled) return;

        // ── Shelf ──
        if (isSignedIn) {
          try {
            const hydrated = await Promise.all(
              rows.map(async (r) => {
                try {
                  const project = await getProject(r.project_id);
                  return { reading: r, project };
                } catch (err) {
                  logger.debug("reading_project_unavailable", err?.message);
                  return { reading: r, project: null };
                }
              }),
            );
            if (cancelled) return;
            setYourStoriesBook(
              hydrated
                .filter((h) => h.project)
                .map(({ reading, project }) => {
                  const pct = Math.max(
                    0,
                    Math.min(100, Number(reading.progress_percent) || 0),
                  );
                  // Total parts shown to the reader = chapter_count
                  // (everything the writer planned), falling back to
                  // published_chapter_count if the legacy field is
                  // absent. Earlier this preferred published_chapter_
                  // count which under-counted projects with drafts —
                  // a writer who saw "Ragaboum has 12 parts" in their
                  // own editor would land on /browse and see "5
                  // parts" on the same project because only 5 were
                  // marked published. The progress bar still tracks
                  // reader position via progress_percent, so picking
                  // the larger total here only affects the "new"-
                  // state caption's headline number, not the bar's
                  // accuracy.
                  const totalParts =
                    project.chapter_count ?? project.published_chapter_count ?? 0;
                  const partsRead = Math.floor((pct / 100) * totalParts);
                  const lastReadIso =
                    reading.last_read_at || reading.started_at;
                  return {
                    id: project.id,
                    img: project.cover_url || "",
                    title: project.title || "",
                    lastReadIso,
                    partsRead,
                    partLeft: Math.max(0, totalParts - partsRead),
                    totalParts,
                  };
                }),
            );
          } catch (error) {
            logger.error("userhome_readings_failed", error?.message);
            if (!cancelled) setYourStoriesBook([]);
          }
        } else if (!cancelled) {
          setYourStoriesBook([]);
        }
        if (!cancelled) setLoadingReadings(false);

        // ── Recommendations ──
        // 1. Filter pool: drop user's own + already on shelf.
        // 2. Score each candidate with multi-signal weighting
        //    (see _scoreProject — locale, taste overlap, rating,
        //    popularity, recency, depth).
        // 3. De-duplicate by author so no single writer dominates.
        // 4. Take the top RECOMMENDED_LIMIT.
        //
        // Replaces the previous ``.slice(0, RECOMMENDED_LIMIT)``
        // which was effectively "first N public projects we
        // happened to fetch" — not actually a recommendation, just
        // the top of the popularity-ordered backend response.
        const shelfIds = new Set(rows.map((r) => String(r.project_id)));
        const allPublic = projectsResp.data.projects || [];

        // Taste signal: collect genres + categories from projects
        // the user has read OR owns. Reader-as-writer reads in
        // their lane, so pulling toward known taste lifts hit-rate.
        const ownGenres = new Set();
        const ownCategories = new Set();
        for (const p of allPublic) {
          if ((p.owner_email || "").toLowerCase() === selfEmail) {
            if (p.genre) ownGenres.add(p.genre.toLowerCase());
            if (p.category) ownCategories.add(p.category.toLowerCase());
          }
        }
        for (const r of rows) {
          const p = allPublic.find((pp) => String(pp.id) === String(r.project_id));
          if (p?.genre) ownGenres.add(p.genre.toLowerCase());
          if (p?.category) ownCategories.add(p.category.toLowerCase());
        }

        const scoringCtx = {
          currentLocale: (locale === "fr" ? "fr" : "en"),
          ownGenres,
          ownCategories,
        };
        const scored = allPublic
          .filter(
            (p) =>
              !shelfIds.has(String(p.id)) &&
              (p.owner_email || "").toLowerCase() !== selfEmail,
          )
          .map((p) => ({ p, score: _scoreProject(p, scoringCtx) }))
          .sort((a, b) => b.score - a.score)
          .map((e) => e.p);

        const filtered = _dedupeByAuthor(scored, MAX_PER_AUTHOR).slice(
          0,
          RECOMMENDED_LIMIT,
        );

        // ``owner_username`` is now embedded in every project row
        // by the backend — no follow-up ``findByEmail`` per author,
        // and (importantly) anonymous visitors get the handle too,
        // so the public catalogue no longer prints raw emails.
        const authorOf = (p) =>
          p.owner_username || p.owner_name || p.owner_email || "";

        // Catalogue pool keeps the *full* public list (with owner
        // handles attached) — the showcase rail filters client-side
        // by category so we don't refetch on tab switch.
        setCataloguePool(
          allPublic.map((p) => ({
            ...p,
            author: authorOf(p),
          })),
        );

        setRecommendedForYouBooks(
          filtered.map((p) => ({
            id: p.id,
            image: p.cover_url || "",
            title: p.title,
            author: authorOf(p),
            totalStars: Number(p.rating_average ?? 0),
            ratingCount: Number(p.rating_count ?? 0),
            totalViews: Number(p.reader_count ?? 0),
            // "Recommended for you" lists other writers' public
            // projects — show what's readable, not their drafts.
            totalParts: p.published_chapter_count ?? p.chapter_count ?? 0,
            description: p.description || "",
            // Status is *not* localised here — it's a flag the
            // renderer translates at render time so changing locale
            // updates every card without a refetch.
            archived: Boolean(p.archived),
            projectRef: null,
          })),
        );
      } finally {
        if (!cancelled) setLoadingRecommended(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // ``locale`` is part of the recommendation scorer so changing
    // language in Settings re-ranks the rail with the new locale boost.
  }, [isSignedIn, locale]);

  return (
    <>
      <Seo
        title={t("nav.browse")}
        path="/browse"
        description={t("home.seo.description")}
      />
      <Navigation searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      {!searchQuery && (
        <Page className="px-5 py-5 md:px-20 md:py-8 md:pb-16">
          {/* Welcome section. Guest variant pitches sign-up +
              read-as-you-are; signed-in variant shows the avatar
              greeter. The catalogue + recommendations rails below
              are identical so the rest of the page reads the same
              for both audiences. */}
          {!isSignedIn ? (
            // Slim guest banner. Single row on desktop, stacks on
            // mobile. No gradient hero — keeps the visual weight on
            // the catalogue + recommendations rails below.
            <div className="px-4 py-3 mb-4 md:mb-6 rounded-xl border border-border bg-surfaceAlt/50 flex flex-col sm:flex-row items-center gap-3 text-sm">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primaryMuted text-primary shrink-0">
                <FontAwesomeIcon icon={faCompass} className="text-sm" />
              </span>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <span className="font-semibold text-text">
                  {t("home.guest.title")}
                </span>{" "}
                <span className="text-secondText">
                  {t("home.guest.subtitle")}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-3 py-1.5 text-sm font-semibold hover:brightness-110 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <FontAwesomeIcon
                    icon={faRightToBracket}
                    className="text-xs"
                  />
                  {t("home.guest.cta_signup")}
                </Link>
                <button
                  type="button"
                  onClick={scrollToFindReading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:border-primary hover:text-primary transition"
                >
                  <FontAwesomeIcon icon={faBookOpen} className="text-xs" />
                  {t("home.guest.cta_browse")}
                </button>
              </div>
            </div>
          ) : (
          <div className="p-3 md:p-6 mb-4 md:mb-10 rounded-xl border border-border app-surface flex flex-col md:flex-row lg:justify-between items-center max-md:gap-6">
            <div className="flex gap-6 items-center">
              {(() => {
                const avatar = (
                  <img
                    src={avatarSrc(user.avatar)}
                    alt=""
                    aria-hidden="true"
                    className="w-16 h-16 rounded-full border border-border bg-surfaceAlt object-cover"
                  />
                );
                // Wire the avatar to the signed-in user's own profile. Shows
                // as a bare <img> when signed out — no dead click target.
                // ``profilePath(user)`` prefers the username so the URL bar
                // doesn't leak the email; falls back to the email when the
                // handle isn't known yet (mid-load).
                return isSignedIn && (user.email || user.name) ? (
                  <Link
                    to={profilePath(user)}
                    aria-label={t("home.welcome.avatar_link")}
                    className="inline-block rounded-full hover:ring-2 hover:ring-primary/40 transition"
                  >
                    {avatar}
                  </Link>
                ) : (
                  avatar
                );
              })()}
              <div className="min-w-0">
                <h3 className="text-sm md:text-2xl text-text font-semibold truncate">
                  {t("home.welcome_back", { name: user.name })}
                </h3>
                <p className="text-[16px]/[24px] text-secondText max-w-prose">
                  {t("home.tagline")}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                size="md"
                variant="primary"
                onClick={() =>
                  navigate(isSignedIn ? "/project" : "/login?next=/project")
                }
                iconLeft={
                  <FontAwesomeIcon icon={faPenNib} className="text-[12px]" />
                }
              >
                {t("home.start_writing")}
              </Button>
              {/* "Start reading" jumps into the most-recently-read
                  project (``yourStoriesBook[0]`` — the shelf is
                  ordered by ``-last_read_at`` server-side). When
                  the shelf is empty, the CTA scrolls the
                  recommendations grid into view so the writer has
                  somewhere to go. The earlier handler hard-coded
                  ``/collaboration`` which is the collab dashboard,
                  not a reader. */}
              <Button
                size="md"
                variant="outline"
                onClick={() => {
                  const next = yourStoriesBook[0];
                  if (next?.id) {
                    navigate(`/read/${next.id}`);
                    return;
                  }
                  // No shelf yet — fall through to the
                  // recommendations grid (most personal) and
                  // then to the catalogue showcase if even that
                  // grid is empty. Same helper used by the
                  // empty-shelf "Find something to read" CTA so
                  // the two affordances stay in lockstep.
                  scrollToFindReading();
                }}
                iconLeft={
                  <FontAwesomeIcon icon={faBook} className="text-[12px]" />
                }
              >
                {t("home.start_reading")}
              </Button>
            </div>
          </div>
          )}

          {/* Catalogue showcase — the page's vitrine. Sits above
              the personal sections so a brand-new reader (or a
              signed-out visitor) immediately gets a sense of the
              breadth of public work on the platform, while the
              Your Stories / Recommended grids below carry the
              personal-feed weight. Reuses the public-projects pool
              that the Recommended fetch already pulled — no extra
              round trip.
              ``catalogueSectionRef`` lets the empty-shelf CTAs
              fall back to scrolling here when Recommended itself
              is empty. */}
          <div ref={catalogueSectionRef} className="scroll-mt-24">
            <CatalogueShowcase
              projects={cataloguePool}
              excludeIds={excludeFromCatalogue}
              loading={loadingRecommended}
            />
          </div>

          {/* Your Stories = current readings. Hidden entirely for
              guests — they don't have a shelf, and the "log in to
              track your reading" empty-state above the
              recommendations grid felt like a wall instead of an
              invitation. The hero strip + the recommendations rail
              already give guests something to do. */}
          {isSignedIn ? (
          <>
          <div className="mb-4 md:mb-6">
            <SectionHeading
              heading={t("home.your_stories.title")}
              text={t("home.your_stories.subtitle")}
            />
          </div>

          {loadingReadings ? (
            <SkeletonGrid count={6} />
          ) : yourStoriesBook.length === 0 ? (
            <EmptyState
              icon={faBookOpen}
              title={t("home.empty.your_stories.title")}
              description={t("home.empty.your_stories.description")}
              action={
                // Same primary-pill shell as the empty-Recommended
                // "Explore the community" CTA further down the
                // page so both empty-state nudges on the browse
                // page wear the same button design. Behaviour is
                // local: scroll to the recommended grid (or the
                // catalogue showcase as a fallback) instead of
                // routing off-page.
                <button
                  type="button"
                  onClick={scrollToFindReading}
                  className="app-button-primary inline-block"
                >
                  {t("home.empty.your_stories.cta")}
                </button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-5 mb-8">
              {yourStoriesBook.map((book) => (
                <BookCard
                  key={book.id}
                  img={book.img}
                  title={book.title}
                  lastReadIso={book.lastReadIso}
                  partsRead={book.partsRead}
                  partLeft={book.partLeft}
                  totalParts={book.totalParts}
                  projectId={book.id}
                />
              ))}
            </div>
          )}
          </>
          ) : null}

          {/* Recommended */}
          <div
            ref={recommendedSectionRef}
            className="mb-4 md:mb-6 flex justify-between items-center scroll-mt-24"
          >
            <SectionHeading
              heading={t("home.recommended.title")}
              text={t("home.recommended.subtitle")}
            />
            {/* "View all" used to point at /collaboration (the
                collab dashboard, not a browse view). The
                recommendations grid below already *is* the full
                view returned by the popularity-ranked list, so the
                button is dropped — no shortcut to add. */}
          </div>

          {loadingRecommended ? (
            <SkeletonGrid count={6} />
          ) : recommendedForYouBooks.length === 0 ? (
            <EmptyState
              icon={faCompass}
              title={t("home.empty.recommended.title")}
              description={t("home.empty.recommended.description")}
              action={
                <Link to="/collaboration" className="app-button-primary inline-block">
                  {t("home.empty.recommended.cta")}
                </Link>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-5 mb-8">
              {recommendedForYouBooks.map((book) => (
                <RecommendBookCard
                  key={book.id}
                  img={book.image}
                  title={book.title}
                  writer={t("book.by_author", { author: book.author })}
                  rating={book.totalStars}
                  ratingCount={book.ratingCount}
                  views={book.totalViews}
                  totalParts={book.totalParts}
                  description={book.description}
                  status={
                    book.archived
                      ? t("home.status.complete")
                      : t("home.status.in_progress")
                  }
                  projectRef={book.projectRef}
                  projectId={book.id}
                />
              ))}
            </div>
          )}
        </Page>
      )}
    </>
  );
};

export default UserHome;
